/* Compare bags (sections/page-template-compare.liquid): two bags side by side from the published specs
 * (harber.compare_specs, numbers in mm / g / L / inches). ?a=&b= keep the pair (shareable, and the product page's
 * specs block links here). Lengths and weights are printed in both unit systems; CSS shows the one picked in the
 * CM | IN switch (theme.js, html[data-units]). */
(() => {
  const root = document.querySelector('[data-compare]');
  const cfgEl = document.querySelector('[data-compare-config]');
  if (!root || !cfgEl) return;
  let cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch { return; }
  const T = cfg.t;
  const products = cfg.products || [];
  const byHandle = new Map(products.map((p) => [p.handle, p]));
  const out = root.querySelector('[data-compare-result]');
  const selects = [...root.querySelectorAll('[data-select]')];
  const heads = [...root.querySelectorAll('[data-slot]')];
  const sticky = root.querySelector('[data-compare-sticky]');
  const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const val = (k) => T.values?.[k] || k;
  const trim = (n) => String(Math.round(n * 10) / 10).replace(/\.0$/, '');

  // ---- Units (both systems printed; CSS shows one) ----------------------------------------------------------------
  const len = (mm) => (mm == null ? null : `<span class="u-m">${trim(mm / 10)} cm</span><span class="u-i">${trim(mm / 25.4)}″</span>`);
  const dims = (z) => {
    if (z.widthMm == null || z.heightMm == null) return null;
    const v = [z.widthMm, z.heightMm, z.depthMm].filter((x) => x != null);
    return `<span class="u-m">${v.map((x) => trim(x / 10)).join(' × ')} cm</span><span class="u-i">${v.map((x) => trim(x / 25.4)).join(' × ')}″</span>`;
  };
  const weight = (g) => (g == null ? null
    : `<span class="u-m">${g < 1000 ? `${Math.round(g)} g` : `${trim(Math.round(g / 10) / 100)} kg`}</span><span class="u-i">${g < 454 ? `${Math.round(g / 28.35)} oz` : `${trim(g / 453.6)} lb`}</span>`);
  const strap = (z) => {
    if (z.strapMinMm == null && z.strapMaxMm == null) return null;
    if (z.strapMinMm != null && z.strapMaxMm != null && z.strapMinMm !== z.strapMaxMm) {
      return `<span class="u-m">${trim(z.strapMinMm / 10)}–${trim(z.strapMaxMm / 10)} cm</span><span class="u-i">${trim(z.strapMinMm / 25.4)}–${trim(z.strapMaxMm / 25.4)}″</span>`;
    }
    return len(z.strapMaxMm ?? z.strapMinMm);
  };

  // ---- The visitor's device (device finder: localStorage 'harber:device' { name, family }) ------------------------
  let device = null;
  try {
    const d = JSON.parse(localStorage.getItem('harber:device') || 'null');
    const inch = Number((/(\d{1,2}(?:\.\d)?)\s*(?:"|”|″|-inch|inch)/i.exec(d?.name || '') || [])[1]);
    if (d?.name && inch >= 7 && inch <= 18 && (d.family === 'macbook' || d.family === 'ipad')) device = { name: d.name, inch, family: d.family };
  } catch { /* storage blocked */ }

  // ---- State -----------------------------------------------------------------------------------------------------
  const params = new URLSearchParams(location.search);
  const pick = [params.get('a'), params.get('b')].map((h) => (h && byHandle.has(h) ? h : null));
  if (!pick[0]) pick[0] = products[0]?.handle || null;
  if (!pick[1] || pick[1] === pick[0]) {
    pick[1] = byHandle.get(pick[0])?.specs.similar?.find((h) => byHandle.has(h) && h !== pick[0]) || products.find((p) => p.handle !== pick[0])?.handle || null;
  }
  const size = [0, 0];
  let view = 'product';
  let onlyDiff = false;

  // Options grouped by category (optgroups), in the order of the categories
  const ORDER = ['backpack', 'briefcase', 'messenger', 'tote', 'holdall', 'sling', 'crossbody', 'organiser', 'pouch', 'other'];
  const groups = ORDER.map((c) => [c, products.filter((p) => (p.specs.category || 'other') === c)]).filter(([, l]) => l.length);
  selects.forEach((sel) => {
    sel.innerHTML = groups.map(([c, l]) => `<optgroup label="${esc(val(c))}">${l.map((p) => `<option value="${esc(p.handle)}">${esc(p.title)}</option>`).join('')}</optgroup>`).join('');
  });

  const sizesOf = (p) => (p?.specs.sizes?.length ? p.specs.sizes : [{}]);
  const current = (i) => {
    const p = byHandle.get(pick[i]);
    if (!p) return null;
    const zs = sizesOf(p);
    return { p, z: zs[Math.min(size[i], zs.length - 1)] };
  };

  // ---- Heads (photo, picker, size chips) --------------------------------------------------------------------------
  const drawHeads = () => {
    heads.forEach((head, i) => {
      const c = current(i);
      selects[i].value = pick[i] || '';
      const media = head.querySelector('[data-media]');
      const chips = head.querySelector('[data-sizes]');
      if (!c) { media.innerHTML = ''; chips.hidden = true; return; }
      const life = view === 'life' && c.p.life;
      media.href = c.p.url;
      media.classList.toggle('is-life', Boolean(life));
      media.innerHTML = `<img src="${esc(life || c.p.image)}" alt="${esc(c.p.title)}" loading="lazy" width="900" height="900">`;
      const zs = sizesOf(c.p);
      chips.hidden = zs.length < 2;
      chips.innerHTML = zs.length < 2 ? '' : zs.map((z, k) => `<button type="button" class="cmp__size" data-size="${k}" aria-pressed="${k === Math.min(size[i], zs.length - 1)}">${esc(z.label || z.size)}</button>`).join('');
    });
    sticky.innerHTML = [0, 1].map((i) => {
      const c = current(i);
      return c ? `<span class="cmp__sticky-item"><img src="${esc(c.p.image)}" alt="" width="36" height="36"><span>${esc(c.p.title)}</span></span>` : '<span></span>';
    }).join('');
  };

  // ---- Rows ------------------------------------------------------------------------------------------------------
  const none = '<span class="cmp__none">—</span>';
  const stat = (value, label) => `<div class="cmp__stat"><p class="cmp__stat-value">${value ?? none}</p>${label ? `<p class="cmp__stat-label">${esc(label)}</p>` : ''}</div>`;
  const pair = (fn, label, keep) => {
    const a = fn(0), b = fn(1);
    if (a == null && b == null && !keep) return '';
    return `<div class="cmp__row">${stat(a, label)}${stat(b, label)}</div>`;
  };
  // Section: a small gold number over the serif title; it eases in on the first view only (redraws stay put)
  let blockNo = 0;
  let drawn = false;
  const block = (title, body, extra = '', dark = false) => {
    if (!body) return '';
    blockNo += 1;
    return `<section class="cmp__block${dark ? ' cmp__block--dark' : ''}"${drawn ? '' : ' data-reveal'}><div class="cmp__block-head"><p class="cmp__no" aria-hidden="true">${String(blockNo).padStart(2, '0')}</p><h2 class="cmp__title">${esc(title)}</h2>${extra}</div>${body}</section>`;
  };

  const laptopIcon = (n) => `<svg viewBox="0 0 44 30" width="44" height="30" aria-hidden="true"><rect x="7" y="3" width="30" height="19" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M2 26h40" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><text x="22" y="15.8" text-anchor="middle" font-size="8.5" font-weight="600" fill="currentColor">${n}″</text></svg>`;
  const tabletIcon = (n) => `<svg viewBox="0 0 28 36" width="26" height="34" aria-hidden="true"><rect x="3" y="2" width="22" height="32" rx="3" fill="none" stroke="currentColor" stroke-width="1.5"/><text x="14" y="21" text-anchor="middle" font-size="7.5" font-weight="600" fill="currentColor">${n}″</text></svg>`;

  const draw = () => {
    const c = [current(0), current(1)];
    if (!c[0] || !c[1]) { out.innerHTML = `<p class="cmp__empty muted">${esc(T.empty)}</p>`; return; }
    const S = (i) => c[i].p.specs;
    const Z = (i) => c[i].z;
    const years = cfg.warrantyYears || 1;
    const warranty = (years === 1 ? T.year : T.years).replace('[n]', years);

    const overview = [
      pair((i) => `${c[i].p.priceVaries ? `<small>${esc(T.from)}</small> ` : ''}${esc(c[i].p.price)}`, T.price),
      pair((i) => (c[i].p.colours.length ? `<span class="cmp__swatches">${c[i].p.colours.map((s) => `<span title="${esc(s.name)}">${s.swatch}</span>`).join('')}</span>` : null), T.colours),
      pair((i) => (Z(i).capacityL != null ? `${trim(Z(i).capacityL)} L` : null), T.capacity, true),
      pair((i) => weight(Z(i).weightG), T.weight),
      pair((i) => (Z(i).pockets != null ? String(Z(i).pockets) : null), T.pockets),
      pair(() => esc(warranty), T.warranty),
    ].join('');

    const measureRows = [pair((i) => dims(Z(i)), T.dimensions, true), pair((i) => strap(Z(i)), T.strap)].join('');
    // Handmade: the figures are approximate (owner, Oct 2026)
    const measures = measureRows && T.measureNote ? `${measureRows}<p class="cmp__note">${esc(T.measureNote)}</p>` : measureRows;

    const fitCell = (i) => {
      const z = Z(i);
      const parts = [];
      if (z.laptopIn != null) parts.push(`<span class="cmp__fit">${laptopIcon(trim(z.laptopIn))}<span>${esc(T.upToLaptop.replace('[s]', `${trim(z.laptopIn)}″`))}</span></span>`);
      if (z.tabletIn != null) parts.push(`<span class="cmp__fit">${tabletIcon(trim(z.tabletIn))}<span>${esc(T.upToTablet.replace('[s]', `${trim(z.tabletIn)}″`))}</span></span>`);
      return `<div class="cmp__stat"><div class="cmp__fits">${parts.length ? parts.join('') : `${none}<span class="cmp__stat-label">${esc(T.noLaptop)}</span>`}</div></div>`;
    };
    let fits = `<div class="cmp__row">${fitCell(0)}${fitCell(1)}</div>`;
    if (device) {
      const ok = (i) => {
        const z = Z(i);
        const max = device.family === 'ipad' ? (z.tabletIn ?? z.laptopIn) : z.laptopIn;
        return max != null && device.inch <= max + 0.05;
      };
      fits += `<p class="cmp__device">${esc(T.yourDevice.replace('[d]', device.name))}</p><div class="cmp__row cmp__row--tight">${[0, 1].map((i) => `<div class="cmp__stat"><span class="cmp__badge ${ok(i) ? 'is-yes' : 'is-no'}">${ok(i) ? `✓ ${esc(T.fitsYes)}` : `✕ ${esc(T.fitsNo)}`}</span></div>`).join('')}</div>`;
    }

    const materials = [
      pair((i) => (S(i).materials?.main?.length ? S(i).materials.main.map((m) => esc(val(m))).join('<br>') : null), T.mainMaterial),
      pair((i) => (S(i).materials?.lining ? esc(val(S(i).materials.lining)) : null), T.lining),
      pair((i) => (S(i).materials?.hardware ? esc(val(S(i).materials.hardware)) : null), T.hardware),
    ].join('');

    // Feature table (after bellroy.com): grey header row per feature, one cell per bag. Rows both bags lack are left out.
    const FLAG_KEYS = ['paddedLaptop', 'handles', 'quickAccess', 'waterBottle', 'luggagePass', 'frontInsert', 'magneticPouches', 'keyClip', 'waterResistant', 'personalisable'];
    const cell = (v) => (v === true ? `<span class="cmp__yes">✓</span>` : v === false || v == null ? none : esc(val(v)));
    const feats = [
      [T.closure, S(0).closure, S(1).closure],
      [T.strapType, S(0).strap, S(1).strap],
      ...FLAG_KEYS.map((f) => [val(f), S(0).flags?.[f], S(1).flags?.[f]]),
    ].filter(([, a, b]) => (a != null && a !== false) || (b != null && b !== false));
    const shown = feats.filter(([, a, b]) => !onlyDiff || String(a ?? false) !== String(b ?? false));
    const featTable = feats.length
      ? `<table class="cmp__table">${shown.map(([label, a, b]) => `<tbody><tr><th colspan="2" scope="colgroup">${esc(label)}</th></tr><tr><td>${cell(a)}</td><td>${cell(b)}</td></tr></tbody>`).join('') || `<tbody><tr><td colspan="2">${none}</td></tr></tbody>`}</table>`
      : '';
    const diffToggle = feats.length ? `<label class="check cmp__diff"><input type="checkbox" data-only-diff${onlyDiff ? ' checked' : ''}><span>${esc(T.onlyDifferences)}</span></label>` : '';

    const lang = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();

    // The key differences: up to four, worked out from the specs of the chosen sizes, so the difference reads at a glance
    // without scrolling the whole page
    const name = (i) => `<b>${esc(c[i].p.title)}</b>`;
    const fill = (tpl, map) => Object.entries(map).reduce((t, [k, v]) => t.split(`[${k}]`).join(v), esc(tpl));
    const diffs = [];
    const la = Z(0).laptopIn, lb = Z(1).laptopIn;
    if (la != null && lb != null && Math.abs(la - lb) >= 0.5) {
      const w = la > lb ? 0 : 1;
      diffs.push({ k: 'laptop', html: fill(T.dLaptop, { name: name(w), s: `${trim(Math.max(la, lb))}″` }), sub: fill(T.dVs, { a: `${trim(Z(w).laptopIn)}″`, b: `${trim(Z(1 - w).laptopIn)}″` }) });
    } else if ((la == null) !== (lb == null)) {
      const w = la != null ? 0 : 1;
      diffs.push({ k: 'laptop', html: fill(T.dOnlyLaptop, { name: name(w), s: `${trim(Z(w).laptopIn)}″` }) });
    } else if (la != null && lb != null) {
      diffs.push({ k: 'laptop', same: true, html: fill(T.dSameLaptop, { s: `${trim(Math.max(la, lb))}″` }) });
    }
    const ca = Z(0).capacityL, cb = Z(1).capacityL;
    if (ca != null && cb != null && Math.abs(ca - cb) >= Math.max(1, 0.1 * Math.min(ca, cb))) {
      const w = ca > cb ? 0 : 1;
      diffs.push({ k: 'capacity', html: fill(T.dCapacity, { name: name(w), n: trim(Math.abs(ca - cb)) }), sub: fill(T.dVs, { a: `${trim(Z(w).capacityL)} L`, b: `${trim(Z(1 - w).capacityL)} L` }) });
    }
    const wa = Z(0).weightG, wb = Z(1).weightG;
    if (wa != null && wb != null && Math.abs(wa - wb) >= 50) {
      const w = wa < wb ? 0 : 1;
      diffs.push({ k: 'weight', html: fill(T.dLighter, { name: name(w), w: weight(Math.abs(wa - wb)) }) });
    }
    const pa = c[0].p.priceMin, pb = c[1].p.priceMin;
    if (pa != null && pb != null && Math.abs(pa - pb) >= 500 && window.Theme?.formatMoney) {
      const w = pa < pb ? 0 : 1;
      diffs.push({ k: 'price', html: fill(T.dCheaper, { name: name(w), p: esc(window.Theme.formatMoney(Math.abs(pa - pb))) }) });
    }
    if (S(0).closure && S(1).closure && S(0).closure !== S(1).closure) diffs.push({ k: 'closure', html: `${esc(T.closure)}: ${fill(T.dVs, { a: esc(val(S(0).closure)), b: esc(val(S(1).closure)) })}` });
    for (const f of ['paddedLaptop', 'luggagePass', 'waterBottle', 'quickAccess', 'waterResistant', 'frontInsert']) {
      const a = S(0).flags?.[f] === true, b = S(1).flags?.[f] === true;
      if (a !== b) diffs.push({ k: f, html: fill(T.dOnly, { name: name(a ? 0 : 1), f: esc(val(f)) }) });
    }
    const real = diffs.filter((d) => !d.same);
    const shownDiffs = (real.length ? diffs : []).slice(0, 4);
    const crux = `<div class="cmp__row cmp__row--crux">${[0, 1].map((i) => {
      const sum = S(i).summary?.[lang] || S(i).summary?.en || '';
      return `<div class="cmp__stat"><p class="cmp__crux">${esc(sum)}</p><a class="cmp__go" href="${esc(c[i].p.url)}">${esc(T.viewProduct)} <span aria-hidden="true">→</span></a></div>`;
    }).join('')}</div>`;
    // The key differences, right under the pickers: a dark block headed like "What fits"
    const diffsBody = shownDiffs.length
      ? `<ul class="cmp-nut__list" role="list">${shownDiffs.map((d) => `<li class="cmp-nut__diff${d.same ? ' is-same' : ''}"><span class="cmp-nut__dot" aria-hidden="true"></span><span>${d.html}${d.sub ? `<small>${d.sub}</small>` : ''}</span></li>`).join('')}</ul>`
      : `<p class="cmp-nut__same">${esc(T.nearlySame)}</p>`;

    blockNo = 0;
    // The key differences open the result; dark bands for what fits (mid-way) and the closing summary
    out.innerHTML = block(T.keyDiffs, diffsBody, '', true).replace('cmp__block--dark', 'cmp__block--dark cmp__block--diffs') + block(T.overview, overview) + block(T.measurements, measures) + block(T.fits, fits, '', true)
      + block(T.materials, materials) + block(T.features, featTable, diffToggle) + block(T.crux, crux, '', true);
    if (!drawn) { drawn = true; window.Theme?.reveal?.(out); }
  };

  const render = () => {
    drawHeads();
    draw();
    const u = new URL(location.href);
    if (pick[0]) u.searchParams.set('a', pick[0]);
    if (pick[1]) u.searchParams.set('b', pick[1]);
    history.replaceState(history.state, '', u);
  };

  // ---- Events ----------------------------------------------------------------------------------------------------
  selects.forEach((sel, i) => sel.addEventListener('change', () => {
    pick[i] = sel.value;
    size[i] = 0;
    if (pick[0] === pick[1]) {
      const other = byHandle.get(pick[i])?.specs.similar?.find((h) => byHandle.has(h)) || products.find((p) => p.handle !== pick[i])?.handle;
      if (other) { pick[1 - i] = other; size[1 - i] = 0; }
    }
    render();
    document.dispatchEvent(new CustomEvent('harber:compare', { detail: { a: pick[0], b: pick[1] } }));
  }));
  root.addEventListener('click', (e) => {
    const sz = e.target.closest('[data-size]');
    if (sz) { size[Number(sz.closest('[data-slot]').dataset.slot)] = Number(sz.dataset.size); render(); return; }
    if (e.target.closest('[data-swap]')) { pick.reverse(); size.reverse(); render(); return; }
    const v = e.target.closest('[data-view]');
    if (v) {
      view = v.dataset.view;
      root.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-pressed', String(b === v)));
      drawHeads();
    }
  });
  root.addEventListener('change', (e) => { if (e.target.matches('[data-only-diff]')) { onlyDiff = e.target.checked; draw(); } });

  // A thin bar with the two names once the photos have scrolled away
  const headsEl = root.querySelector('[data-compare-heads]');
  if ('IntersectionObserver' in window && headsEl) {
    new IntersectionObserver(([en]) => sticky.classList.toggle('is-on', !en.isIntersecting && en.boundingClientRect.top < 0)).observe(headsEl);
  }

  if (!products.length) {
    headsEl.hidden = true;
    root.querySelector('.cmp__bar').hidden = true;
    out.innerHTML = `<p class="cmp__empty muted">${esc(T.empty)}</p>`;
    return;
  }
  render();
  document.dispatchEvent(new CustomEvent('harber:compare', { detail: { a: pick[0], b: pick[1] } }));
})();
